import { registerSW } from "virtual:pwa-register";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type InstallAvailabilityListener = () => void;

export function isStandaloneDisplay():boolean {
  return matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIosDevice():boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export class PwaManager {
  private installPrompt?: BeforeInstallPromptEvent;
  private readonly installListeners = new Set<InstallAvailabilityListener>();
  private updateServiceWorker?: (reloadPage?: boolean) => Promise<void>;
  private registration?: ServiceWorkerRegistration;
  private updatePromptVisible = false;
  private applyingUpdate = false;

  get canInstall():boolean { return Boolean(this.installPrompt) && !isStandaloneDisplay(); }
  get shouldShowIosInstallHelp():boolean { return isIosDevice() && !isStandaloneDisplay(); }

  start():void {
    window.addEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
    window.addEventListener("appinstalled", this.onAppInstalled);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.updateServiceWorker = registerSW({
      immediate: true,
      onNeedRefresh: () => this.showUpdatePrompt(),
      onRegisteredSW: (_scriptUrl, registration) => { this.registration = registration; },
      onRegisterError: (error) => console.warn("[PWA] Pendaftaran service worker gagal.", error),
    });
  }

  onInstallAvailability(listener:InstallAvailabilityListener):()=>void {
    this.installListeners.add(listener);
    return () => this.installListeners.delete(listener);
  }

  async promptInstall():Promise<boolean> {
    const prompt = this.installPrompt;
    if (!prompt || isStandaloneDisplay()) return false;
    this.installPrompt = undefined;
    this.notifyInstallAvailability();
    await prompt.prompt();
    const choice = await prompt.userChoice;
    return choice.outcome === "accepted";
  }

  dispose():void {
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", this.onAppInstalled);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.installListeners.clear();
  }

  private readonly onBeforeInstallPrompt=(event:Event):void=>{
    event.preventDefault();
    this.installPrompt=event as BeforeInstallPromptEvent;
    this.notifyInstallAvailability();
  };

  private readonly onAppInstalled=():void=>{
    this.installPrompt=undefined;
    this.notifyInstallAvailability();
  };

  private readonly onVisibilityChange=():void=>{
    if(!document.hidden)void this.registration?.update().catch(()=>undefined);
  };

  private notifyInstallAvailability():void { for(const listener of this.installListeners)listener(); }

  private showUpdatePrompt():void {
    if(this.updatePromptVisible||this.applyingUpdate)return;
    this.updatePromptVisible=true;
    const notice=document.createElement("aside");
    notice.className="pwa-update-notice";
    notice.setAttribute("role","status");
    notice.setAttribute("aria-live","polite");
    notice.innerHTML=`<span>Kemas kini permainan tersedia</span><div><button class="pwa-update-later">NANTI</button><button class="pwa-update-apply">KEMAS KINI</button></div>`;
    const close=()=>{this.updatePromptVisible=false;notice.remove();};
    notice.querySelector(".pwa-update-later")?.addEventListener("click",close,{once:true});
    notice.querySelector<HTMLButtonElement>(".pwa-update-apply")?.addEventListener("click",async(event)=>{
      if(this.applyingUpdate)return;
      this.applyingUpdate=true;
      const button=event.currentTarget as HTMLButtonElement;button.disabled=true;button.textContent="MENGEMAS KINI...";
      try{await this.updateServiceWorker?.(true);close();}
      catch(error){console.warn("[PWA] Kemas kini gagal.",error);this.applyingUpdate=false;button.disabled=false;button.textContent="CUBA LAGI";}
    });
    document.body.append(notice);
  }
}
